/**
 * 자재 관리 섹션 — 층별 수량 매트릭스.
 *
 * 행 = 품목, 열 = 층(기본 B1 + "층 추가"), 셀 = 그 층에 들어간 수량.
 * 마지막 열은 품목별 전 층 총합, 마지막 행은 층별 합계 + 전체 총합.
 *
 * 데이터 모델: SiteMaterial 레코드 하나 = (층 × 품목) 수량.
 *   - 자재가 하나도 없으면 기본 품목 4종(단방향 카메라 CCTV·전방위 카메라
 *     CCTV·조명·무선 비상벨)을 B1 층에 자동 생성(시드)한다.
 *   - 층 추가: 현재 품목 전부에 대해 그 층의 0 수량 레코드 생성.
 *   - 품목 추가: 현재 층 전부에 대해 0 수량 레코드 생성. 품목 삭제는 전 층 삭제.
 *   - 셀 수정: 해당 레코드 quantity 갱신(레코드가 없으면 생성).
 *
 * admin/editor 만 쓰기 가능. 엑셀 일괄 등록은 기존 흐름 유지(층 미지정 → B1).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Stack,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  DeleteOutline as DeleteIcon,
  Inventory2 as InventoryIcon,
  UploadFile as UploadFileIcon,
  Layers as LayersIcon,
} from '@mui/icons-material';
import {
  SiteMaterialService,
  SiteMaterial,
  CreateSiteMaterialRequest,
} from '../../services/SiteMaterialService';
import { getUserInfo } from '../../utils/auth';
import MaterialsImportDialog from './MaterialsImportDialog';
import {
  parseExcelWorkbook,
  extractSheetAsGrid,
  ParsedWorkbook,
  SheetGrid,
} from './materialsExcelImport';

/** 기본 품목 — 현장에 들어가는 자재가 정해져 있어 첫 진입 시 자동 시드. */
export const DEFAULT_MATERIAL_ITEMS = [
  '단방향 카메라 CCTV',
  '전방위 카메라 CCTV',
  '조명',
  '무선 비상벨',
];

const DEFAULT_FLOOR = 'B1';

interface Props {
  projectId: number;
}

function canEdit(): boolean {
  const u = getUserInfo();
  return u?.role === 'admin' || u?.role === 'editor';
}

/** 레거시(층 도입 전) 레코드는 floor_name 이 비어 있음 → B1 취급. */
const floorOf = (m: SiteMaterial): string => m.floor_name?.trim() || DEFAULT_FLOOR;

const cellKey = (floor: string, name: string) => `${floor}|${name}`;

const MaterialsSection: React.FC<Props> = ({ projectId }) => {
  const [items, setItems] = useState<SiteMaterial[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false); // 최초 목록 로드 완료 여부(시드 게이트)
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false); // 층/품목 추가·삭제 등 다건 API 작업 중

  // 셀 편집 draft — key: `${floor}|${name}`, 커밋(blur/Enter) 시 API 반영 후 제거.
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  // 층/품목 추가 다이얼로그.
  const [floorDialogOpen, setFloorDialogOpen] = useState(false);
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [dialogName, setDialogName] = useState('');
  const [dialogError, setDialogError] = useState<string | null>(null);

  // 엑셀 일괄 등록 흐름 — 파일 → 시트 선택 → 미리보기 → POST 진행률.
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [parsing, setParsing] = useState(false);
  const [pendingWorkbook, setPendingWorkbook] = useState<ParsedWorkbook | null>(null);
  const [pendingSheetName, setPendingSheetName] = useState<string | null>(null);
  const [importGrid, setImportGrid] = useState<SheetGrid | null>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(null);
  const [snack, setSnack] = useState<{ msg: string; severity: 'success' | 'info' | 'warning' | 'error' } | null>(null);

  const writable = useMemo(() => canEdit(), []);
  const seededRef = useRef(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await SiteMaterialService.list(projectId);
      setItems(data);
      setLoaded(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : '자재 목록 조회 실패');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // 자재가 하나도 없으면 기본 품목을 B1 에 자동 시드(1회, admin/editor 만).
  // 반드시 최초 목록 로드(loaded)가 끝난 뒤에만 — 그렇지 않으면 마운트 직후
  // 빈 items 상태에서 이미 자재가 있는 현장에도 기본 품목을 중복 생성한다.
  useEffect(() => {
    if (!loaded || loading || error || !writable || items.length > 0 || seededRef.current) return;
    seededRef.current = true;
    (async () => {
      try {
        for (const name of DEFAULT_MATERIAL_ITEMS) {
          await SiteMaterialService.create(projectId, {
            name,
            quantity: 0,
            floor_name: DEFAULT_FLOOR,
          });
        }
        await refresh();
      } catch (e) {
        setSnack({
          msg: e instanceof Error ? e.message : '기본 품목 생성 실패',
          severity: 'error',
        });
      }
    })();
  }, [loaded, loading, error, writable, items.length, projectId, refresh]);

  // ---- 매트릭스 파생 상태 ---------------------------------------------------
  const floors = useMemo(() => {
    const set = new Set<string>();
    items.forEach((m) => set.add(floorOf(m)));
    if (set.size === 0) set.add(DEFAULT_FLOOR);
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'ko', { numeric: true }));
  }, [items]);

  const names = useMemo(() => {
    const set = new Set<string>();
    items.forEach((m) => set.add(m.name));
    if (set.size === 0) DEFAULT_MATERIAL_ITEMS.forEach((n) => set.add(n));
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'ko', { numeric: true }));
  }, [items]);

  const cellRecord = useCallback(
    (floor: string, name: string): SiteMaterial | undefined =>
      items.find((m) => floorOf(m) === floor && m.name === name),
    [items],
  );

  const cellQty = (floor: string, name: string): number => cellRecord(floor, name)?.quantity ?? 0;
  const rowTotal = (name: string): number => floors.reduce((s, f) => s + cellQty(f, name), 0);
  const floorTotal = (floor: string): number => names.reduce((s, n) => s + cellQty(floor, n), 0);
  const grandTotal = names.reduce((s, n) => s + rowTotal(n), 0);

  // ---- 셀 편집 --------------------------------------------------------------
  const commitCell = async (floor: string, name: string) => {
    const key = cellKey(floor, name);
    const raw = drafts[key];
    if (raw === undefined) return;
    const qty = parseInt(raw, 10);
    if (Number.isNaN(qty) || qty < 0) {
      setSnack({ msg: '수량은 0 이상 정수여야 합니다.', severity: 'warning' });
      setDrafts((d) => {
        const { [key]: _drop, ...rest } = d;
        return rest;
      });
      return;
    }
    const record = cellRecord(floor, name);
    if (record && record.quantity === qty) {
      setDrafts((d) => {
        const { [key]: _drop, ...rest } = d;
        return rest;
      });
      return;
    }
    try {
      if (record) {
        await SiteMaterialService.update(record.id, { quantity: qty });
      } else {
        await SiteMaterialService.create(projectId, { name, quantity: qty, floor_name: floor });
      }
      await refresh();
    } catch (e) {
      setSnack({ msg: e instanceof Error ? e.message : '수량 저장 실패', severity: 'error' });
    } finally {
      setDrafts((d) => {
        const { [key]: _drop, ...rest } = d;
        return rest;
      });
    }
  };

  // ---- 층 / 품목 추가·삭제 --------------------------------------------------
  const openFloorDialog = () => {
    setDialogName('');
    setDialogError(null);
    setFloorDialogOpen(true);
  };

  const openItemDialog = () => {
    setDialogName('');
    setDialogError(null);
    setItemDialogOpen(true);
  };

  const handleAddFloor = async () => {
    const floor = dialogName.trim();
    if (!floor) {
      setDialogError('층 이름을 입력해 주세요. (예: B2)');
      return;
    }
    if (floors.includes(floor)) {
      setDialogError('이미 있는 층입니다.');
      return;
    }
    setBusy(true);
    setDialogError(null);
    try {
      // 현재 품목 전부에 대해 새 층의 0 수량 레코드를 만들어 층을 영속화.
      for (const name of names) {
        await SiteMaterialService.create(projectId, { name, quantity: 0, floor_name: floor });
      }
      setFloorDialogOpen(false);
      await refresh();
      setSnack({ msg: `${floor} 층이 추가되었습니다.`, severity: 'success' });
    } catch (e) {
      setDialogError(e instanceof Error ? e.message : '층 추가 실패');
    } finally {
      setBusy(false);
    }
  };

  const handleAddItem = async () => {
    const name = dialogName.trim();
    if (!name) {
      setDialogError('품목 이름을 입력해 주세요.');
      return;
    }
    if (names.includes(name)) {
      setDialogError('이미 있는 품목입니다.');
      return;
    }
    setBusy(true);
    setDialogError(null);
    try {
      for (const floor of floors) {
        await SiteMaterialService.create(projectId, { name, quantity: 0, floor_name: floor });
      }
      setItemDialogOpen(false);
      await refresh();
      setSnack({ msg: `'${name}' 품목이 추가되었습니다.`, severity: 'success' });
    } catch (e) {
      setDialogError(e instanceof Error ? e.message : '품목 추가 실패');
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteItem = async (name: string) => {
    if (!window.confirm(`'${name}' 품목을 모든 층에서 삭제하시겠습니까?`)) return;
    setBusy(true);
    try {
      const targets = items.filter((m) => m.name === name);
      for (const t of targets) {
        await SiteMaterialService.delete(t.id);
      }
      await refresh();
      setSnack({ msg: `'${name}' 품목이 삭제되었습니다.`, severity: 'success' });
    } catch (e) {
      setSnack({ msg: e instanceof Error ? e.message : '품목 삭제 실패', severity: 'error' });
    } finally {
      setBusy(false);
    }
  };

  // ---- 엑셀 일괄 등록 (기존 흐름 유지 — 층 미지정 → B1) ----------------------
  const handleFilePick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setParsing(true);
    try {
      const parsed = await parseExcelWorkbook(file);
      if (parsed.sheetNames.length === 0) {
        setSnack({ msg: '엑셀에 시트가 없습니다.', severity: 'warning' });
        return;
      }
      if (parsed.sheetNames.length === 1) {
        openImportForSheet(parsed, parsed.sheetNames[0]);
      } else {
        setPendingWorkbook(parsed);
        setPendingSheetName(parsed.sheetNames[0]);
      }
    } catch (err) {
      setSnack({
        msg: err instanceof Error ? err.message : '파일 파싱 실패',
        severity: 'error',
      });
    } finally {
      setParsing(false);
    }
  };

  const openImportForSheet = (parsed: ParsedWorkbook, sheet: string) => {
    const grid = extractSheetAsGrid(parsed.workbook, sheet);
    if (grid.headers.length === 0 || grid.dataRows.length === 0) {
      setSnack({ msg: '시트에 데이터가 없습니다.', severity: 'warning' });
      return;
    }
    setImportGrid(grid);
    setPendingSheetName(sheet);
    setImportDialogOpen(true);
    setPendingWorkbook(null);
  };

  const handleConfirmSheet = () => {
    if (!pendingWorkbook || !pendingSheetName) return;
    openImportForSheet(pendingWorkbook, pendingSheetName);
  };

  const handleCancelSheet = () => {
    setPendingWorkbook(null);
    setPendingSheetName(null);
  };

  const handleImportClose = () => {
    if (bulkSubmitting) return;
    setImportDialogOpen(false);
    setImportGrid(null);
    setPendingSheetName(null);
  };

  const handleBulkConfirm = async (payloads: CreateSiteMaterialRequest[]) => {
    setBulkSubmitting(true);
    setBulkProgress({ done: 0, total: payloads.length });
    let success = 0;
    const failures: string[] = [];
    for (let i = 0; i < payloads.length; i++) {
      try {
        await SiteMaterialService.create(projectId, payloads[i]);
        success += 1;
      } catch (err) {
        failures.push(`${payloads[i].name}: ${err instanceof Error ? err.message : '등록 실패'}`);
      }
      setBulkProgress({ done: i + 1, total: payloads.length });
    }
    setBulkSubmitting(false);
    setBulkProgress(null);
    setImportDialogOpen(false);
    setImportGrid(null);
    setPendingSheetName(null);
    await refresh();
    if (failures.length === 0) {
      setSnack({ msg: `자재 ${success}건 등록 완료`, severity: 'success' });
    } else {
      setSnack({
        msg: `${success}건 등록 / ${failures.length}건 실패 (${failures[0]}${failures.length > 1 ? ' 외' : ''})`,
        severity: 'warning',
      });
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, mt: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
        <InventoryIcon color="primary" />
        <Typography variant="h6" fontWeight={700}>
          자재 관리
        </Typography>
        <Chip size="small" label={`품목 ${names.length} · 층 ${floors.length}`} variant="outlined" />
        <Box sx={{ flex: 1 }} />
        {writable && (
          <>
            <Button
              startIcon={parsing ? <CircularProgress size={14} color="inherit" /> : <UploadFileIcon />}
              variant="outlined"
              onClick={() => fileInputRef.current?.click()}
              disabled={parsing || bulkSubmitting || busy}
            >
              {parsing ? '읽는 중…' : '엑셀/CSV 업로드'}
            </Button>
            <Button startIcon={<AddIcon />} variant="outlined" onClick={openItemDialog} disabled={busy}>
              품목 추가
            </Button>
            <Button startIcon={<LayersIcon />} variant="contained" onClick={openFloorDialog} disabled={busy}>
              층 추가
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
              style={{ display: 'none' }}
              onChange={handleFilePick}
            />
          </>
        )}
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        층별로 들어간 자재 수량을 관리합니다. 셀에 수량을 입력하면 바로 저장되고,
        품목별 총합과 층별 합계가 자동 계산됩니다.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" sx={{ minWidth: 480 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, minWidth: 180 }}>품목</TableCell>
                {floors.map((f) => (
                  <TableCell key={f} align="right" sx={{ fontWeight: 700, minWidth: 96 }}>
                    {f}
                  </TableCell>
                ))}
                <TableCell align="right" sx={{ fontWeight: 700, minWidth: 96, bgcolor: 'action.hover' }}>
                  총합
                </TableCell>
                {writable && <TableCell align="right" sx={{ width: 56 }} />}
              </TableRow>
            </TableHead>
            <TableBody>
              {names.map((name) => (
                <TableRow key={name} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{name}</TableCell>
                  {floors.map((f) => {
                    const key = cellKey(f, name);
                    return (
                      <TableCell key={f} align="right">
                        {writable ? (
                          <TextField
                            size="small"
                            type="number"
                            inputProps={{
                              min: 0,
                              style: { textAlign: 'right', padding: '4px 8px', width: 72 },
                              'aria-label': `${name} ${f} 수량`,
                            }}
                            value={drafts[key] ?? String(cellQty(f, name))}
                            onChange={(e) =>
                              setDrafts((d) => ({ ...d, [key]: e.target.value }))
                            }
                            onBlur={() => void commitCell(f, name)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                            }}
                          />
                        ) : (
                          cellQty(f, name).toLocaleString('ko-KR')
                        )}
                      </TableCell>
                    );
                  })}
                  <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'action.hover' }}>
                    {rowTotal(name).toLocaleString('ko-KR')}
                  </TableCell>
                  {writable && (
                    <TableCell align="right">
                      <Tooltip title="품목 삭제 (모든 층)">
                        <span>
                          <IconButton size="small" onClick={() => void handleDeleteItem(name)} disabled={busy}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {/* 층별 합계 행 */}
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 700 }}>층별 합계</TableCell>
                {floors.map((f) => (
                  <TableCell key={f} align="right" sx={{ fontWeight: 700 }}>
                    {floorTotal(f).toLocaleString('ko-KR')}
                  </TableCell>
                ))}
                <TableCell align="right" sx={{ fontWeight: 800 }}>
                  {grandTotal.toLocaleString('ko-KR')}
                </TableCell>
                {writable && <TableCell />}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* 층 추가 다이얼로그 */}
      <Dialog open={floorDialogOpen} onClose={() => !busy && setFloorDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>층 추가</DialogTitle>
        <DialogContent dividers>
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {dialogError}
            </Alert>
          )}
          <TextField
            label="층 이름"
            autoFocus
            fullWidth
            value={dialogName}
            onChange={(e) => setDialogName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void handleAddFloor();
            }}
            placeholder="예: B2"
            helperText="현재 품목 전체가 새 층에 0 수량으로 생성됩니다."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFloorDialogOpen(false)} disabled={busy}>
            취소
          </Button>
          <Button onClick={() => void handleAddFloor()} variant="contained" disabled={busy}>
            {busy ? '추가 중...' : '추가'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 품목 추가 다이얼로그 */}
      <Dialog open={itemDialogOpen} onClose={() => !busy && setItemDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>품목 추가</DialogTitle>
        <DialogContent dividers>
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {dialogError}
            </Alert>
          )}
          <TextField
            label="품목 이름"
            autoFocus
            fullWidth
            value={dialogName}
            onChange={(e) => setDialogName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void handleAddItem();
            }}
            placeholder="예: 스피커"
            helperText="모든 층에 0 수량으로 생성됩니다."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setItemDialogOpen(false)} disabled={busy}>
            취소
          </Button>
          <Button onClick={() => void handleAddItem()} variant="contained" disabled={busy}>
            {busy ? '추가 중...' : '추가'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 다중 시트 선택 — 단일 시트면 곧바로 미리보기로 점프하므로 이 단계는 건너뜀. */}
      <Dialog open={!!pendingWorkbook} onClose={handleCancelSheet} maxWidth="xs" fullWidth>
        <DialogTitle>엑셀 시트 선택</DialogTitle>
        <DialogContent dividers>
          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel id="material-sheet-select-label">시트</InputLabel>
            <Select
              labelId="material-sheet-select-label"
              label="시트"
              value={pendingSheetName ?? ''}
              onChange={(e) => setPendingSheetName(e.target.value as string)}
            >
              {(pendingWorkbook?.sheetNames ?? []).map((n) => (
                <MenuItem key={n} value={n}>
                  {n}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelSheet}>취소</Button>
          <Button onClick={handleConfirmSheet} variant="contained" disabled={!pendingSheetName}>
            미리보기
          </Button>
        </DialogActions>
      </Dialog>

      <MaterialsImportDialog
        open={importDialogOpen}
        grid={importGrid}
        sheetName={pendingSheetName}
        onConfirm={handleBulkConfirm}
        onClose={handleImportClose}
        submitting={bulkSubmitting}
      />

      {/* 일괄 등록 진행률 — 다이얼로그 닫힌 뒤 단발성 표시(보통은 다이얼로그 안에서 끝남). */}
      {bulkSubmitting && bulkProgress && (
        <Snackbar
          open
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          message={`등록 중 ${bulkProgress.done}/${bulkProgress.total}…`}
        />
      )}

      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {snack ? (
          <Alert severity={snack.severity} onClose={() => setSnack(null)} variant="filled">
            {snack.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Paper>
  );
};

export default MaterialsSection;
